import { useEffect, useRef } from "react";
import type { CSSProperties } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useAuthStore } from "../state/authStore";
import { renderSetupValue, type SetupItem, type SetupSectionData, type UnitSystem } from "../utils/setupView";
import TireVisual from "./TireVisual";
import type { TireData } from "../types";

interface SetupModalProps {
  sections: SetupSectionData[];
  setupName: string | null;
  tireData?: TireData | null;
  unit?: string;
  hasSto: boolean;
  stoFilename: string | null;
  isOwnRun: boolean;
  stoBusy: "download" | "upload" | null;
  stoStatus: string | null;
  onDownload: () => void;
  onAttach: (file: File) => void;
  onClose: () => void;
}

/**
 * Garage-faithful setup viewer modal — the read surface for a run's full
 * CarSetup. Sections stack vertically and each corner pair is a wide auto-fit
 * grid so labels print in FULL (no truncation). Web "Download .sto" lives in
 * the header; the word "Install" is reserved for the desktop agent. Units are
 * driven by the global Settings pref (no per-view toggle).
 */
export default function SetupModal({
  sections,
  setupName,
  tireData,
  unit,
  hasSto,
  stoFilename,
  isOwnRun,
  stoBusy,
  stoStatus,
  onDownload,
  onAttach,
  onClose,
}: SetupModalProps) {
  const user = useAuthStore((s) => s.user);
  const system: UnitSystem = user?.preferences?.units === "metric" ? "metric" : "standard";
  const dialogRef = useRef<HTMLDivElement>(null);

  // Accessibility floor: scroll-lock, focus-trap, ESC, focus restore.
  useEffect(() => {
    const prevActive = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialogRef.current?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const focusables = dialogRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (!focusables || focusables.length === 0) return;
      const list = Array.from(focusables);
      const first = list[0];
      const last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      prevActive?.focus?.();
    };
  }, [onClose]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <div style={st.overlay} onClick={onClose}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="setup-modal-title"
        tabIndex={-1}
        style={st.dialog}
        onClick={(e) => e.stopPropagation()}
      >
        <header style={st.header}>
          <div style={st.headerText}>
            <h3 id="setup-modal-title" style={st.title}>Setup values</h3>
            <div style={st.subtitle}>{setupName || stoFilename || "This run's setup"}</div>
          </div>
          <div style={st.headerActions}>
            {hasSto && (
              <button type="button" className="btn btn-primary" style={st.headerBtn} onClick={onDownload} disabled={stoBusy !== null}>
                {stoBusy === "download" ? "Downloading…" : "Download .sto"}
              </button>
            )}
            {isOwnRun && (
              <label className={`btn btn-secondary${stoBusy ? " is-disabled" : ""}`} style={{ ...st.headerBtn, cursor: "pointer", margin: 0 }}>
                {stoBusy === "upload" ? "Attaching…" : hasSto ? "Replace .sto" : "Attach .sto"}
                <input
                  type="file"
                  accept=".sto"
                  style={{ display: "none" }}
                  disabled={stoBusy !== null}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = "";
                    if (f) onAttach(f);
                  }}
                />
              </label>
            )}
            <button type="button" aria-label="Close" style={st.closeBtn} onClick={onClose}>
              ×
            </button>
          </div>
        </header>

        <div style={st.toolbar}>
          {(hasSto || isOwnRun) && (
            <span style={st.helper}>Installs via the Desktop Agent → Team Setups</span>
          )}
          <Link to="/settings" style={st.unitsLink}>
            Units: {system === "metric" ? "Metric" : "US"} · change in Settings
          </Link>
        </div>
        {stoStatus && <div style={st.stoStatus}>{stoStatus}</div>}

        <div style={st.body}>
          {tireData && tireData.confidence !== "none" && (
            <TireVisual tireData={tireData} unit={unit ?? "standard"} />
          )}
          {sections.map((section) => (
            <section key={section.name} style={st.section}>
              <h4 style={st.sectionTitle}>{section.name}</h4>
              {section.pairs.map((pair, pi) => (
                <div key={pair.label || `pair-${pi}`} style={st.pair}>
                  {pair.label && <div style={st.pairLabel}>{pair.label}</div>}
                  <div style={st.pairGrid}>
                    <CornerCell label={pair.left.label} items={pair.left.items} system={system} />
                    <CornerCell label={pair.right.label} items={pair.right.items} system={system} />
                  </div>
                </div>
              ))}
              {section.shared.length > 0 && (
                <div style={st.shared}>
                  {section.shared.map((item) => (
                    <ValueRow key={item.flatKey} item={item} system={system} />
                  ))}
                </div>
              )}
            </section>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function CornerCell({ label, items, system }: { label: string; items: SetupItem[]; system: UnitSystem }) {
  return (
    <div style={st.cornerCell}>
      <div style={st.cornerLabel}>{label}</div>
      {items.map((item) => (
        <ValueRow key={item.flatKey} item={item} system={system} />
      ))}
    </div>
  );
}

function ValueRow({ item, system }: { item: SetupItem; system: UnitSystem }) {
  return (
    <div style={st.row}>
      <span style={st.param}>{item.displayKey}</span>
      <span style={st.value}>{renderSetupValue(item.flatKey, item.value, system)}</span>
    </div>
  );
}

const st: Record<string, CSSProperties> = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(2, 6, 23, 0.82)",
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "1.25rem",
  },
  dialog: {
    width: "min(900px, 100%)",
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
    background: "var(--ink-1, #0b1220)",
    border: "1px solid var(--rule)",
    borderRadius: "14px",
    boxShadow: "0 28px 80px rgba(2, 6, 23, 0.6)",
    outline: "none",
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "1rem",
    padding: "1.1rem 1.25rem",
    borderBottom: "1px solid var(--rule-faint)",
  },
  headerText: { minWidth: 0 },
  title: {
    fontFamily: "var(--font-display)",
    fontWeight: 700,
    fontSize: "1.15rem",
    color: "var(--text-strong)",
    margin: 0,
  },
  subtitle: {
    fontFamily: "var(--font-mono)",
    fontSize: "0.8125rem",
    color: "var(--text-secondary)",
    marginTop: "0.25rem",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  headerActions: { display: "flex", alignItems: "center", gap: "0.6rem", flexShrink: 0, flexWrap: "wrap" },
  headerBtn: { padding: "0.4rem 0.85rem", fontSize: "0.82rem" },
  closeBtn: {
    background: "none",
    border: "none",
    color: "var(--text-secondary)",
    cursor: "pointer",
    fontSize: "1.5rem",
    lineHeight: 1,
    padding: "0 0.25rem",
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "0.75rem",
    padding: "0.55rem 1.25rem",
    borderBottom: "1px solid var(--rule-faint)",
    flexWrap: "wrap",
  },
  helper: { fontFamily: "var(--font-body)", fontSize: "0.8125rem", color: "var(--text-muted)" },
  unitsLink: { fontFamily: "var(--font-body)", fontSize: "0.8125rem", color: "var(--text-secondary)", textDecoration: "none" },
  stoStatus: {
    padding: "0.5rem 1.25rem",
    fontSize: "0.8125rem",
    color: "var(--live-strong)",
    borderBottom: "1px solid var(--rule-faint)",
  },
  body: {
    overflowY: "auto",
    padding: "1rem 1.25rem 1.25rem",
    display: "flex",
    flexDirection: "column",
    gap: "1.4rem",
  },
  section: { display: "flex", flexDirection: "column", gap: "0.75rem" },
  sectionTitle: {
    fontFamily: "var(--font-display)",
    fontSize: "0.82rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "var(--live-strong)",
    margin: 0,
    paddingBottom: "0.4rem",
    borderBottom: "1px solid var(--rule-faint)",
  },
  pair: { display: "flex", flexDirection: "column", gap: "0.4rem" },
  pairLabel: {
    fontFamily: "var(--font-display)",
    fontSize: "0.8125rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "var(--text-muted)",
  },
  pairGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "0.75rem" },
  cornerCell: {
    background: "rgba(255, 255, 255, 0.02)",
    border: "1px solid var(--rule-faint)",
    borderRadius: "8px",
    padding: "0.6rem 0.75rem",
  },
  cornerLabel: {
    fontFamily: "var(--font-mono)",
    fontSize: "0.8125rem",
    fontWeight: 700,
    letterSpacing: "0.05em",
    color: "var(--live-strong)",
    textAlign: "center",
    paddingBottom: "0.4rem",
    marginBottom: "0.2rem",
    borderBottom: "1px solid var(--rule-faint)",
  },
  shared: {
    display: "flex",
    flexDirection: "column",
    maxWidth: "460px",
    background: "rgba(255, 255, 255, 0.02)",
    border: "1px solid var(--rule-faint)",
    borderRadius: "8px",
    padding: "0.5rem 0.75rem",
  },
  row: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    gap: "1rem",
    padding: "0.26rem 0",
  },
  param: {
    fontFamily: "var(--font-body)",
    fontSize: "0.82rem",
    color: "var(--text-secondary)",
    minWidth: 0,
  },
  value: {
    fontFamily: "var(--font-mono)",
    fontVariantNumeric: "tabular-nums",
    fontSize: "0.84rem",
    fontWeight: 600,
    color: "var(--text-strong)",
    textAlign: "right",
    whiteSpace: "nowrap",
  },
};
